<div class="student-card-wrapper" [attr.data-student-enrollment-id]="student?.studentEnrollmentId">
  @if (!isExpanded) {
    <div class="student-header">
      <h5>{{ student?.studentFirstName }} {{ student?.studentLastName }}</h5>
      <button
        type="button"
        mat-icon-button
        (click)="toggleExpand.emit(student?.studentEnrollmentId || '')"
        [attr.aria-label]="isExpanded ? 'Collapse' : 'Expand'">
        @if (isExpanded) {
          <img class="chevron" [src]="chevronUpPath" width="30" height="30" alt="collapse" />
        }
        @if (!isExpanded) {
          <img class="chevron" [src]="chevronDownPath" width="30" height="30" alt="expand" />
        }
      </button>
    </div>
  }
  @if (isExpanded && (!editable || formReady)) {
    <div class="student-content" [formGroup]="form">
      <!-- Parent Information Row - spans full width -->
      <div class="parent-info-row">
        <h6 class="column-header">Parent Contact Information</h6>
        <div class="parent-info-grid">
          <div class="info-item">
            <div class="field-label">Email Address</div>
            <p>{{ student?.parentEmailAddress || '—' }}</p>
          </div>
          <div class="info-item">
            <div class="field-label">Parent Cell Phone</div>
            <p>{{ student?.parentCellPhone || '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Column Grid for Academic Status and Required Tuition/Fees -->
      <div class="columns-grid">
        <!-- Academic Information -->
        <div class="info-column">
          <h6 class="column-header">Academic Status</h6>
          @if (editable) {
            <div class="info-item">
              <div class="field-label">K12 School Grade Level</div>
              @if (gradeInDropdown || preKSelected) {
                <mat-form-field
                  appearance="outline"
                  [class.field-locked]="form.get('schoolTermGradeId')?.disabled">
                  <mat-select formControlName="schoolTermGradeId" (selectionChange)="onGradeChange()">
                    <mat-option [value]="PRE_K_VALUE">Pre-K</mat-option>
                    @for (grade of schoolTermGrades; track grade.schoolTermGradeId) {
                      <mat-option [value]="grade.schoolTermGradeId">{{ grade.gradeName }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              } @else {
                <div class="readonly-input">{{ student?.gradeLevel || '—' }}</div>
              }
            </div>
            <div class="info-item">
              <div class="field-label">Attendance Type</div>
              <mat-form-field appearance="outline">
                <mat-select
                  formControlName="attendanceTypeId"
                  (selectionChange)="onAttendanceTypeChange()"
                  placeholder="Select attendance type">
                  @for (type of attendanceTypes; track type.attendanceTypeId) {
                    <mat-option [value]="type.attendanceTypeId">{{ type.name }}</mat-option>
                  }
                </mat-select>
                @if (form.get('attendanceTypeId')?.hasError('required') && form.get('attendanceTypeId')?.touched) {
                  <mat-error>Attendance Type is required</mat-error>
                }
              </mat-form-field>
            </div>
          } @else {
            <div class="info-item">
              <div class="field-label">K12 School Grade Level</div>
              <div class="readonly-input">{{ student?.gradeLevel || '—' }}</div>
            </div>
            <div class="info-item">
              <div class="field-label">Attendance Type</div>
              <div class="readonly-input">{{ student?.attendanceType || '—' }}</div>
            </div>
          }
        </div>

        <!-- Required Tuition/Fees — shared header spanning two sub-columns -->
        <div class="tuition-fees-group">
          <h6 class="column-header">Required Tuition/Fees</h6>
          <div class="tuition-fees-columns">
            <!-- Cost sub-column -->
            <div class="info-column">
              @if (editable) {
                <div class="info-item">
                  <div class="field-label">Standard Cost</div>
                  <div class="money-input-container">
                    <input
                      class="money-input"
                      type="text"
                      formControlName="academicYearStandardCostTotal"
                      (input)="onMoneyInput($event, 'academicYearStandardCostTotal')"
                      (keydown)="onMoneyKeydown($event)"
                      (paste)="onMoneyPaste($event, 'academicYearStandardCostTotal')"
                      (blur)="onMoneyBlur('academicYearStandardCostTotal')"
                      (change)="onStandardCostChange()"
                      placeholder="0"
                      step="1"
                      min="0" />
                    <span class="money-input-prefix">$</span>
                  </div>
                </div>
                <div class="info-item">
                  <div class="field-label">Individual Cost</div>
                  <div class="money-input-container">
                    <input
                      class="money-input"
                      type="text"
                      formControlName="academicYearIndividualCostTotal"
                      (input)="onMoneyInput($event, 'academicYearIndividualCostTotal')"
                      (keydown)="onMoneyKeydown($event)"
                      (paste)="onMoneyPaste($event, 'academicYearIndividualCostTotal')"
                      (blur)="onMoneyBlur('academicYearIndividualCostTotal')"
                      (change)="onIndividualCostChange()"
                      placeholder="0"
                      step="1"
                      min="0" />
                    <span class="money-input-prefix">$</span>
                  </div>
                  @if (individualCostErrors.length > 0) {
                    <div class="validation-errors">
                      @for (error of individualCostErrors; track $index) {
                        <p class="error-message">{{ error }}</p>
                      }
                    </div>
                  }
                  @if (preKSelected) {
                    <p class="error-message prek-error">Pre-K is ineligible for the program.</p>
                  }
                </div>
              } @else {
                <div class="info-item">
                  <div class="field-label">Standard Cost</div>
                  <div class="readonly-input">{{ formatCurrency(student?.academicYearStandardCostTotal) }}</div>
                </div>
                <div class="info-item">
                  <div class="field-label">Individual Cost</div>
                  <div class="readonly-input">{{ formatCurrency(student?.academicYearIndividualCostTotal) }}</div>
                </div>
              }
            </div>

            <!-- Term cost sub-column -->
            <div class="info-column">
              @for (term of sortedTerms; track term.schoolTermId) {
                @if (editable) {
                  <div class="info-item">
                    <div class="field-label">{{ term.termName }} Individual Cost</div>
                    <div class="money-input-container">
                      <input
                        class="money-input"
                        type="text"
                        [formControlName]="'termCost_' + term.schoolTermId"
                        (input)="onMoneyInput($event, 'termCost_' + term.schoolTermId)"
                        (keydown)="onMoneyKeydown($event)"
                        (paste)="onMoneyPaste($event, 'termCost_' + term.schoolTermId)"
                        (blur)="onMoneyBlur('termCost_' + term.schoolTermId)"
                        placeholder="0"
                        step="1"
                        min="0" />
                      <span class="money-input-prefix">$</span>
                    </div>
                  </div>
                } @else {
                  <div class="info-item">
                    <div class="field-label">{{ term.termName }} Individual Cost</div>
                    <div class="readonly-input">{{ formatCurrency(term.payment) }}</div>
                  </div>
                }
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Content projection for action components -->
      <ng-content select="[studentCardActions]"></ng-content>
    </div>
  }
</div>
